/**
 * THE GIFT CARD SCREENS — rendered by PortalView like every other screen, with
 * every label and colour coming from the same slots list.
 *
 * Share uses the phone's own share sheet (`navigator.share`, the same pattern
 * as pages/reviewShield/MedicalReferralCard.jsx) with a copy-link button as the
 * fallback. Nothing here sends an SMS or an email.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Share2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { BackBar, BigButton, Field, Title } from './ui';
import { money as sharedMoney, moneyFieldProps, plain } from '../../lib/money';

const money = (v) => sharedMoney(v);
const giftLink = (code) => `${window.location.origin}/voucher/${code}`;

export const GiftCardFace = ({ card, theme = {}, testid }) => (
    <div className="rounded-2xl bg-white/5 p-4" data-testid={testid}>
        <div className="flex items-baseline justify-between">
            <span className="text-2xl font-bold" style={{ color: theme.accent }}>
                {money(card.value)}
            </span>
            <span className="text-xs opacity-70" data-testid={`${testid}-code`}>{card.code}</span>
        </div>
        {card.friend_first_name && (
            <p className="mt-1 text-sm opacity-80">{card.friend_first_name}</p>
        )}
        {card.terms && <p className="mt-2 text-xs opacity-70">{card.terms}</p>}
    </div>
);

export const ShareRow = ({ card, theme, text, paint, preview }) => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const link = giftLink(card.code);

    const share = async () => {
        if (preview) return;
        try {
            if (navigator.share) {
                await navigator.share({ title: text('give_title'), text: card.terms, url: link });
                return;
            }
        } catch { /* the person closed the sheet — nothing to do */ }
        copy();
    };
    const copy = async () => {
        if (preview) return;
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2500);
        } catch { /* clipboard blocked — the code is on screen anyway */ }
    };

    return (
        <div className="space-y-2">
            <BigButton preview={preview} theme={theme} {...paint('give_share')} onClick={share}
                       testid={`loyalty-share-${card.id}`}>
                <span className="inline-flex items-center gap-2">
                    <Share2 className="h-4 w-4" /> {text('give_share')}
                </span>
            </BigButton>
            <BigButton preview={preview} tone="quiet" theme={theme} {...paint('give_copy')} onClick={copy}
                       testid={`loyalty-copy-${card.id}`}>
                <span className="inline-flex items-center gap-2">
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    {copied ? t('loyalty.gift.copied', 'Link copied') : text('give_copy')}
                </span>
            </BigButton>
        </div>
    );
};

export const GiftScreens = ({ screen, gifts, theme, text, shown, paint, form, on,
                             preview, error, busy }) => {
    const { t } = useTranslation();
    const back = <BackBar onBack={() => (preview ? null : on.go?.('gifts'))}
                          label={t('common.back', 'Back')} />;
    const data = gifts || { bank: [], redeem: [], history: [], allowance_left: 0 };

    if (screen === 'gifts') {
        return (
            <div className="space-y-4" data-testid="loyalty-gifts-screen">
                <BackBar onBack={() => (preview ? null : on.go?.('vip'))}
                         label={t('common.back', 'Back')} />
                <Title theme={theme} testid="loyalty-gifts-title">{text('gifts_title')}</Title>
                {shown('gifts_bank') && (
                    <BigButton preview={preview} theme={theme} {...paint('gifts_bank')} testid="loyalty-gifts-bank"
                               onClick={() => (preview ? null : on.go?.('bank'))}>
                        {text('gifts_bank')}
                    </BigButton>
                )}
                {(shown('gifts_create') || data.can_pay_with_points) && (
                    <BigButton preview={preview} theme={theme} {...paint('gifts_create')} testid="loyalty-gifts-create"
                               onClick={() => (preview ? null : on.go?.('create'))}>
                        {text('gifts_create')}
                    </BigButton>
                )}
                {shown('gifts_history') && (
                    <BigButton preview={preview} theme={theme} {...paint('gifts_history')} testid="loyalty-gifts-history"
                               onClick={() => (preview ? null : on.go?.('history'))}>
                        {text('gifts_history')}
                    </BigButton>
                )}
            </div>
        );
    }

    if (screen === 'bank') {
        return (
            <div className="space-y-4" data-testid="loyalty-bank-screen">
                {back}
                <Title theme={theme}>{text('bank_title')}</Title>
                {shown('bank_give') && (
                    <BigButton preview={preview} theme={theme} {...paint('bank_give')} testid="loyalty-bank-give"
                               onClick={() => (preview ? null : on.go?.('give'))}>
                        {text('bank_give')}
                    </BigButton>
                )}
                {shown('bank_redeem') && (
                    <BigButton preview={preview} theme={theme} {...paint('bank_redeem')} testid="loyalty-bank-redeem"
                               onClick={() => (preview ? null : on.go?.('redeem'))}>
                        {text('bank_redeem')}
                    </BigButton>
                )}
            </div>
        );
    }

    if (screen === 'give') {
        return (
            <div className="space-y-4" data-testid="loyalty-give-screen">
                <BackBar onBack={() => (preview ? null : on.go?.('bank'))}
                         label={t('common.back', 'Back')} />
                <Title theme={theme}>{text('give_title')}</Title>
                {!data.bank.length && (
                    <p className="text-center text-sm opacity-70" data-testid="loyalty-bank-empty">
                        {text('give_empty')}
                    </p>
                )}
                {data.bank.map((card) => (
                    <div key={card.id} className="space-y-2">
                        <GiftCardFace card={card} theme={theme} testid={`loyalty-bank-card-${card.id}`} />
                        {form.giving === card.id ? (
                            <>
                                <Field label={text('give_friend_label')}
                                       testid={`loyalty-friend-name-${card.id}`}
                                       value={form.friend_first_name || ''}
                                       onChange={(e) => on.setForm?.({ ...form,
                                           friend_first_name: e.target.value })} />
                                {error && <p className="text-sm text-rose-300"
                                             data-testid="loyalty-error">{error}</p>}
                                <BigButton preview={preview} theme={theme} {...paint('give_share')} disabled={busy || preview}
                                           testid={`loyalty-give-confirm-${card.id}`}
                                           onClick={() => on.giveAway?.(card.id)}>
                                    {text('give_share')}
                                </BigButton>
                            </>
                        ) : card.given_at ? (
                            <>
                                <p className="text-xs opacity-70"
                                   data-testid={`loyalty-given-${card.id}`}>
                                    {t('loyalty.gift.givenWaiting', 'Given, waiting to be used')}
                                </p>
                                <ShareRow card={card} theme={theme} text={text} paint={paint}
                                          preview={preview} />
                            </>
                        ) : (
                            <BigButton preview={preview} tone="quiet" theme={theme}
                                       testid={`loyalty-give-start-${card.id}`}
                                       onClick={() => on.setForm?.({ ...form, giving: card.id })}>
                                {text('give_title')}
                            </BigButton>
                        )}
                    </div>
                ))}
            </div>
        );
    }

    if (screen === 'redeem') {
        return (
            <div className="space-y-4" data-testid="loyalty-redeem-screen">
                <BackBar onBack={() => (preview ? null : on.go?.('bank'))}
                         label={t('common.back', 'Back')} />
                <Title theme={theme}>{text('redeem_title')}</Title>
                {!data.redeem.length && (
                    <p className="text-center text-sm opacity-70" data-testid="loyalty-redeem-empty">
                        {text('redeem_empty')}
                    </p>
                )}
                {data.redeem.map((card) => (
                    <div key={card.id} className="space-y-2">
                        <GiftCardFace card={card} theme={theme} testid={`loyalty-redeem-card-${card.id}`} />
                        {/* DRAWN HERE. The code never leaves this device: no
                            outside website is asked to make the picture. */}
                        <div className="mx-auto w-fit rounded-lg bg-white p-2"
                             data-testid={`loyalty-qr-${card.id}`}>
                            <QRCodeSVG value={giftLink(card.code)} size={160} level="M" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    const allowanceScreen = () => (
        <div className="space-y-4" data-testid="loyalty-create-screen">
            {back}
            <Title theme={theme}>{text('create_title')}</Title>
            <p className="text-sm opacity-80" data-testid="loyalty-allowance-left">
                {text('create_allowance')} {money(data.allowance_left)}
            </p>
            <Field label={text('create_amount_label')}
                   testid="loyalty-create-amount"
                   {...moneyFieldProps(form.value || '',
                       (next) => on.setForm?.({ ...form, value: next }))} />
            <Field label={text('give_friend_label')} testid="loyalty-create-friend"
                   value={form.friend_first_name || ''}
                   onChange={(e) => on.setForm?.({ ...form, friend_first_name: e.target.value })} />
            {data.terms && <p className="text-xs opacity-70"
                              data-testid="loyalty-create-terms">{data.terms}</p>}
            {error && <p className="text-sm text-rose-300" data-testid="loyalty-error">{error}</p>}
            {shown('create_submit') && (
                <BigButton preview={preview} theme={theme} {...paint('create_submit')} disabled={busy || preview}
                           testid="loyalty-create-submit" onClick={() => on.createGift?.()}>
                    {text('create_submit')}
                </BigButton>
            )}
            {form.created && (
                <div className="space-y-2" data-testid="loyalty-created-card">
                    <GiftCardFace card={form.created} theme={theme} testid="loyalty-new-card" />
                    <ShareRow card={form.created} theme={theme} text={text} paint={paint}
                              preview={preview} />
                </div>
            )}
        </div>
    );

    /** HER OWN POINTS, DOLLAR FOR DOLLAR. The new balance appears the moment an
     *  amount is typed; the points only move when she accepts. */
    const pointsCardScreen = () => {
        const pointValue = Number(data.point_value || 0);
        const points = Number(data.points || 0);
        const amount = Number(plain(form.value) || 0);
        const cost = pointValue > 0 && amount > 0 ? Math.round(amount / pointValue) : 0;
        const left = points - cost;
        const presets = (data.amount_presets || []).filter(
            (v) => !data.per_card_max || Number(v) <= Number(data.per_card_max));
        return (
            <div className="space-y-4" data-testid="loyalty-points-card-screen">
                {back}
                <Title theme={theme}>{text('points_card_title')}</Title>
                <div className="rounded-2xl bg-white/5 p-4" data-testid="loyalty-points-card-balance">
                    <p className="text-sm opacity-80">{text('points_card_balance')}</p>
                    <p className="text-2xl font-bold" style={{ color: theme.accent }}>
                        {points.toLocaleString()} · {money(points * pointValue)}
                    </p>
                </div>
                {presets.length > 0 && (
                    <div className="flex flex-wrap gap-2" data-testid="loyalty-points-card-presets">
                        {presets.map((v) => (
                            <button key={v} type="button"
                                    data-testid={`loyalty-points-card-preset-${v}`}
                                    onClick={() => (preview ? null : on.setForm?.({ ...form, value: String(v) }))}
                                    className="min-h-[44px] rounded-xl border-2 px-4 text-sm font-semibold"
                                    style={{ borderColor: theme.accent,
                                             color: Number(plain(form.value)) === Number(v) ? '#fff' : theme.accent,
                                             background: Number(plain(form.value)) === Number(v) ? theme.accent : 'transparent' }}>
                                {money(v)}
                            </button>
                        ))}
                    </div>
                )}
                <Field label={text('create_amount_label')}
                       testid="loyalty-points-card-amount"
                       {...moneyFieldProps(form.value || '',
                           (next) => on.setForm?.({ ...form, value: next }))} />
                {amount > 0 && (
                    <div className="rounded-2xl bg-white/5 p-4" data-testid="loyalty-points-card-after">
                        <p className="text-sm opacity-80">{text('points_card_after')}</p>
                        <p className="text-xl font-bold" data-testid="loyalty-points-card-new-balance">
                            {left.toLocaleString()} · {money(Math.max(0, left) * pointValue)}
                        </p>
                        <p className="mt-1 text-xs opacity-70" data-testid="loyalty-points-card-cost">
                            −{cost.toLocaleString()} {t('loyalty.points', 'points')}
                        </p>
                    </div>
                )}
                <Field label={text('give_friend_label')} testid="loyalty-points-card-friend"
                       value={form.friend_first_name || ''}
                       onChange={(e) => on.setForm?.({ ...form, friend_first_name: e.target.value })} />
                {data.terms && <p className="text-xs opacity-70"
                                  data-testid="loyalty-points-card-terms">{data.terms}</p>}
                {error && <p className="text-sm text-rose-300" data-testid="loyalty-error">{error}</p>}
                {shown('points_card_submit') && (
                    <BigButton preview={preview} theme={theme} {...paint('points_card_submit')}
                               disabled={busy || preview || !(amount > 0) || left < 0}
                               testid="loyalty-points-card-submit"
                               onClick={() => on.createGiftFromPoints?.()}>
                        {text('points_card_submit')}
                    </BigButton>
                )}
                {form.created && (
                    <div className="space-y-2" data-testid="loyalty-points-card-created">
                        <GiftCardFace card={form.created} theme={theme} testid="loyalty-new-points-card" />
                        <ShareRow card={form.created} theme={theme} text={text} paint={paint}
                                  preview={preview} />
                    </div>
                )}
            </div>
        );
    };

    if (screen === 'create') {
        // Her own points come first when the clinic turns that on; the
        // allowance screen is untouched and still the way in when it is off.
        return data.can_pay_with_points ? pointsCardScreen() : allowanceScreen();
    }

    if (screen === 'create_allowance') return allowanceScreen();
    if (screen === 'points_card') return pointsCardScreen();

    if (screen === 'history') {
        return (
            <div className="space-y-3" data-testid="loyalty-history-screen">
                {back}
                <Title theme={theme}>{text('history_title')}</Title>
                {!data.history.length && (
                    <p className="text-center text-sm opacity-70" data-testid="loyalty-history-empty">
                        {text('history_empty')}
                    </p>
                )}
                {data.history.map((card) => (
                    <div key={card.id} className="rounded-xl bg-white/5 p-3 text-sm"
                         data-testid={`loyalty-history-${card.id}`}>
                        <div className="flex justify-between">
                            <span>{card.friend_first_name || '—'}</span>
                            <span className="font-semibold">{money(card.value)}</span>
                        </div>
                        <div className="mt-1 flex justify-between text-xs opacity-70">
                            <span>{(card.given_at || card.issued_at || '').slice(0, 10)}</span>
                            <span data-testid={`loyalty-history-paid-${card.id}`}>
                                {card.paid_with_points
                                    ? t('loyalty.gift.paidWithPoints', 'Created to be redeemed with points')
                                    : ''}
                            </span>
                            <span data-testid={`loyalty-history-status-${card.id}`}>
                                {card.redeemed_at
                                    ? `${t('loyalty.gift.used', 'Used')} ${card.redeemed_at.slice(0, 10)}`
                                    : t('loyalty.gift.notUsed', 'Not used yet')}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return null;
};
